import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  inject,
  signal,
  TrackByFunction,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { ReactiveFormsModule } from '@angular/forms';
import { MatOption } from '@angular/material/autocomplete';
import { FormlyModule } from '@ngx-formly/core';
import { MatSelect } from '@angular/material/select';
import { distinctUntilChanged, isObservable, merge, Observable, takeUntil } from 'rxjs';
import { injectDestroy } from 'ngxtension/inject-destroy';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatInput } from '@angular/material/input';
import { DynamicOptionsService } from './services/dynamic-options.service';
import { isEmptyOptionValue } from './services/option-value.util';
import { FormlyErrorComponent } from '../formly-error.component';
import { FieldLabelRowComponent } from './field-label-row.component';
import { FormlyErrorService } from '../../services/formly.service';
import { DebugService } from 'app/core/services/debug.service';
import { FormlyDataCyDirective } from '../../directives/formly-data-cy.directive';
import { SelectFieldSuffixComponent } from '../select-field-suffix.component';
import { ClearableFieldType } from './clearable-field.type';

@Component({
  selector: 'app-custom-select',
  template: `
    <div class="" [class.hidden]="isFieldHidden()">
      @if (!isDisabled()) {
        <mat-form-field
          class="inline-block w-full"
          [class.mat-form-field-invalid]="formControl.invalid && formControl.touched">
          @if (isLoading()) {
            <mat-spinner class="absolute right-8" diameter="24"> </mat-spinner>
          }
          <mat-label>
            <app-field-label-row [label]="props.label" [required]="props.required" [info]="props.info"></app-field-label-row>
          </mat-label>
          <mat-select
            [required]="props.required"
            [placeholder]="props.placeholder || ''"
            [multiple]="props.multiple || false"
            [formControl]="formControl"
            [formlyAttributes]="field"
            [formlyDataCy]="field"
            (focus)="getLatestOptions()"
            [compareWith]="compareObjects">
            @for (option of availableOptions(); track trackByOption($index, option)) {
              <mat-option
                class="truncate"
                [value]="option.value"
                [disabled]="option.disabled === true">
                <div class="flex items-center gap-2">
                  <span class="min-w-0 flex-1 truncate">{{ option.label }}</span>
                  @if (option.unavailableReason) {
                    <span class="ml-auto shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] leading-none bg-warning bg-opacity-12 text-warning-800">
                      {{ option.unavailableReason }}
                    </span>
                  }
                </div>
              </mat-option>
            }
            @if (availableOptions()?.length === 0) {
              <mat-option disabled="true"> Geen opties beschikbaar </mat-option>
            }
          </mat-select>
          <app-select-field-suffix
            matSuffix
            [field]="field"
            [hasValue]="hasClearableValue()"
            [disabled]="isClearDisabled()"
            [ariaLabel]="clearAriaLabel()"
            (clearSelection)="clear()" />
          @if (field.props.description) {
            <mat-hint>{{ field.props.description }}</mat-hint>
          }
        </mat-form-field>
        @if (field.formControl.errors) {
          <app-formly-error [field]="field"></app-formly-error>
        }
      } @else {
        <mat-form-field class="w-full">
          @if (props.label) {
            <mat-label>
              <app-field-label-row [label]="props.label" [required]="props.required"></app-field-label-row>
            </mat-label>
          }
          <input [disabled]="true" type="text" matInput [value]="displayValue()" class="form-control" />
        </mat-form-field>
      }
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormlyModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatOption,
    FormlyModule,
    MatSelect,
    MatProgressSpinner,
    MatInput,
    FormlyErrorComponent,
    FormlyDataCyDirective,
    SelectFieldSuffixComponent,
    FieldLabelRowComponent,
  ],
  styles: `
    ::ng-deep .mat-mdc-select-arrow {
      opacity: 0 !important;
    }

    ::ng-deep .mat-form-field-disabled .mat-mdc-select-arrow-wrapper {
      display: none;
    }

    mat-form-field:focus,
    mat-form-field:active,
    mat-form-field:focus-within {
      mat-icon {
        color: var(--fuse-primary);
      }
    }
  `,
})
export class CustomSelectComponent extends ClearableFieldType implements OnInit, OnDestroy {
  private destroy$ = injectDestroy();
  private dynamicOptionsService = inject(DynamicOptionsService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  // Signals for reactive state management
  availableOptions = signal<Array<{ label: string; value: any }>>([]);
  linkedData = signal([]);
  private dynamicFilterValue = signal(undefined);
  isLoading = signal(false);
  loaded = signal(false);
  // Track if all options have been loaded (for lazy loading)
  private allOptionsLoaded = signal(false);
  private dynamicFilterGeneration = 0;
  // Tracks the value ID we're currently fetching for validation, to prevent duplicate requests
  private validatingValueKey: string | null = null;

  // Computed values
  isFieldHidden = computed(
    () => this.props.hideIfOptionsEmpty && (this.availableOptions()?.length === 0 || this.dynamicFilterValue() == null)
  );

  /** Deviates: this gates the read-only display branch, so a form-level disable must count too; the clear affordance uses isClearDisabled(). */
  override isDisabled(): boolean {
    return !!this.field.props.disabled || this.form.disabled;
  }

  isClearDisabled(): boolean {
    return this.isDisabled() || this.formControl.disabled;
  }

  // TrackBy function for better performance
  trackByOption: TrackByFunction<any> = (index: number, option: any) => {
    return option.value?.id || option.value || option.label || index;
  };
  optionsBasedOnOtherField = computed(() => {
    return this.field.props?.optionsArray?.some(optionsItem => {
      return optionsItem.filters?.some(optionsFilter => optionsFilter.basedOn === 'dynamic');
    });
  });

  displayValue = computed(() => {
    const value = this.field.formControl.value;
    if (value?.name) return value.name;
    if (value?.selectedInput) return value.selectedInput;
    if (Array.isArray(value)) {
      return value
        .map(v => this.availableOptions().find(opt => this.compareObjects(opt.value, v))?.label)
        .filter(Boolean)
        .join(', ');
    }
    return value;
  });
  private cleanup?: () => void;

  constructor() {
    super();
  }

  ngOnInit() {
    // Check if options come via expressions (auto-detect or explicit flag)
    const hasExpressions = this.field.expressions?.['props.options'] != null;
    const hasExpressionFlag = this.field.props?.expressionsOptions === true;

    if (hasExpressions || hasExpressionFlag) {
      // Options come from expressions - wait for them to be evaluated
      this.initializeFromExpressions();
      this.setupValueChanges();
      return;
    }

    this.initializeDynamicOptions();
    this.initializeOptionsLazy();
    this.setupValueChanges();
  }

  /**
   * Initialize options that come from expressions (like props.options via expressions).
   * This handles cases where options are provided via field.expressions['props.options']
   * or where expressionsOptions flag is set.
   */
  private initializeFromExpressions(): void {
    this.availableOptions.set([]);
    this.isLoading.set(true);

    // Wait for expressions to be evaluated, then set options
    setTimeout(() => {
      const options = this.field.props?.options;
      if (Array.isArray(options)) {
        this.availableOptions.set(options);
        this.isLoading.set(false);
        this.loaded.set(true);
      } else if (isObservable(options)) {
        this.setObserableValue(options as Observable<any>);
      } else {
        // Options not yet available, mark as loaded anyway
        this.isLoading.set(false);
        this.loaded.set(true);
      }
    }, 100);
  }

  /**
   * Lazy loading strategy for options:
   * - No value: Don't fetch anything, wait for focus
   * - Has value: Fetch only the current value to validate it exists
   * - Full options are loaded on focus/click
   */
  private initializeOptionsLazy(): void {
    const currentValue = this.field.formControl?.value;

    if (isEmptyOptionValue(currentValue)) {
      // No initial value - don't load anything, wait for focus
      this.isLoading.set(false);
      this.loaded.set(true);
      return;
    }

    // Has initial value - fetch only that single option to validate
    const filterGeneration = this.dynamicFilterGeneration;
    this.isLoading.set(true);
    this.dynamicOptionsService.fetchSingleOptionByValue(this.field, currentValue).subscribe({
      next: options => {
        if (filterGeneration !== this.dynamicFilterGeneration) return;
        this.availableOptions.set(options);
        this.validateOptionExists();
        this.isLoading.set(false);
        this.loaded.set(true);
      },
      error: () => {
        if (filterGeneration !== this.dynamicFilterGeneration) return;
        this.isLoading.set(false);
        this.loaded.set(true);
      },
    });
  }

  /**
   * Load all options - called on focus/click when user interacts with select
   */
  private loadAllOptions(): void {
    if (this.allOptionsLoaded()) {
      return; // Already loaded all options
    }

    this.isLoading.set(true);
    this.dynamicOptionsService.initializeOptions(this.field).subscribe({
      next: options => {
        this.availableOptions.set(options);
        this.allOptionsLoaded.set(true);
        this.validateOptionExists();
        this.isLoading.set(false);
      },
      error: () => {
        this.isLoading.set(false);
      },
    });
  }

  private initializeDynamicOptions(): void {
    if (!this.optionsBasedOnOtherField()) {
      return;
    }

    const { linkedData, cleanup } = this.dynamicOptionsService.initializeDynamicOptions(
      {
        field: this.field,
        lazyLoad: true, // Skip initial fetch, load options on focus
        onOptionsUpdate: options => {
          this.availableOptions.set(options);
          this.isLoading.set(false);
          this.loaded.set(true);
          this.validateOptionExists();
        },
        onFilterValueUpdate: value => {
          this.dynamicFilterGeneration++;
          // Clear stale options and show loading when parent field changes
          if (this.dynamicFilterValue() !== undefined && this.dynamicFilterValue() !== value) {
            this.availableOptions.set([]);
            this.isLoading.set(true);
          }
          this.dynamicFilterValue.set(value);
        },
      },
      this.destroy$
    );

    this.linkedData.set(linkedData);
    this.cleanup = cleanup;
  }

  private setupValueChanges(): void {
    merge(this.formControl.valueChanges, this.formControl.statusChanges)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.changeDetectorRef.markForCheck());

    this.field.formControl.valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$)).subscribe(() => {
      if (this.dynamicFilterValue()) {
        this.isLoading.set(true);
        this.dynamicOptionsService
          .fetchOptionsForValue(this.dynamicFilterValue(), this.linkedData(), '', this.field)
          .subscribe(options => {
            this.availableOptions.set(options);
            this.isLoading.set(false);
            this.validateOptionExists();
          });
      } else {
        this.validateOptionExists();
      }
    });
  }

  private validateOptionExists(): void {
    // Skip validation for expression-based options (auto-detected or explicit flag)
    const hasExpressions = this.field.expressions?.['props.options'] != null;
    if (hasExpressions || this.field.props?.expressionsOptions === true) {
      return;
    }

    const currentValue = this.field.formControl.value;

    if (isEmptyOptionValue(currentValue) || !this.loaded()) {
      return;
    }

    // Fast path: value(s) are in the currently loaded options → validate normally (clears error)
    const isValueLoaded = Array.isArray(currentValue)
      ? currentValue.every(val => this.availableOptions().some(opt => this.compareObjects(opt.value, val)))
      : this.availableOptions().some(opt => this.compareObjects(opt.value, currentValue));

    if (isValueLoaded) {
      this.field = FormlyErrorService.validateOptions(this.field, this.availableOptions(), currentValue);
      return;
    }

    // Value not found in loaded options. Before declaring it invalid, try to fetch it
    // by ID using fetchSingleOptionByValue / fetchMultipleOptionsByValues. This handles
    // paginated lazy-loaded options where the current value might simply not be in the
    // first page (e.g. after a route reorder, or when the field was just initialized
    // with a saved value).
    this.#fetchAndValidateCurrentValue(currentValue);
  }

  #fetchAndValidateCurrentValue(currentValue: any): void {
    // Manual options are static — fetching is not possible, fall through to normal validation
    if (!this.field?.props?.optionsArray) {
      this.field = FormlyErrorService.validateOptions(this.field, this.availableOptions(), currentValue);
      return;
    }

    // Build a stable key to dedupe concurrent validations for the same value
    const valueKey = Array.isArray(currentValue)
      ? currentValue.map(v => v?.id ?? v).join(',')
      : (currentValue?.id ?? currentValue);
    if (this.validatingValueKey === valueKey) {
      return;
    }
    this.validatingValueKey = valueKey;

    const fetch$ = Array.isArray(currentValue)
      ? this.dynamicOptionsService.fetchMultipleOptionsByValues(this.field, currentValue)
      : this.dynamicOptionsService.fetchSingleOptionByValue(this.field, currentValue);

    fetch$.pipe(takeUntil(this.destroy$)).subscribe({
      next: options => {
        this.validatingValueKey = null;

        // Merge fetched options into availableOptions without removing existing ones,
        // so the dropdown still shows the originally loaded set when the user opens it.
        if (options.length > 0) {
          const existingKeys = new Set(
            this.availableOptions()
              .map(o => o.value?.id ?? o.value)
              .filter(v => v != null)
          );
          const newOptions = options.filter(o => {
            const key = o.value?.id ?? o.value;
            return key != null && !existingKeys.has(key);
          });
          if (newOptions.length > 0) {
            this.availableOptions.update(current => [...current, ...newOptions]);
          }
        }

        // Now validate against the (possibly merged) options list
        this.field = FormlyErrorService.validateOptions(this.field, this.availableOptions(), currentValue);
      },
      error: () => {
        this.validatingValueKey = null;
        // Fetch failed — fall through to normal validation against existing options
        this.field = FormlyErrorService.validateOptions(this.field, this.availableOptions(), currentValue);
      },
    });
  }

  compareObjects(o1: any, o2: any): boolean {
    if (o1 === o2) return true;
    if (!o1 || !o2) return false;
    if (typeof o1 === 'string' && typeof o2 === 'string') return o1 === o2;
    return o1.id === o2.id;
  }

  getLatestOptions() {
    // For expression-based options (auto-detected or explicit flag)
    const hasExpressions = this.field.expressions?.['props.options'] != null;
    const hasExpressionFlag = this.field.props?.expressionsOptions === true;

    if (hasExpressions || hasExpressionFlag) {
      const options = this.props.options;
      if (Array.isArray(options)) {
        this.availableOptions.set(options);
      } else if (isObservable(options)) {
        this.setObserableValue(options as Observable<any>);
      }
      return;
    }

    // For regular options, load all options on focus (lazy loading)
    if (!this.optionsBasedOnOtherField()) {
      this.loadAllOptions();
    } else if (this.dynamicFilterValue() != null) {
      // For dynamic fields, fetch options using the dependent field's value
      this.isLoading.set(true);
      this.dynamicOptionsService
        .fetchOptionsForValue(this.dynamicFilterValue(), this.linkedData(), '', this.field)
        .subscribe(options => {
          this.availableOptions.set(options);
          this.isLoading.set(false);
          this.loaded.set(true);
        });
    }
  }

  setObserableValue(observable: Observable<any>) {
    if (isObservable(observable)) {
      this.isLoading.set(true);
      observable.pipe(takeUntil(this.destroy$)).subscribe({
        next: (options: Array<{ label: string; value: any }>) => {
          this.availableOptions.set(Array.isArray(options) ? options : []);
          this.isLoading.set(false);
          this.validateOptionExists();
        },
        error: () => {
          this.isLoading.set(false);
          this.availableOptions.set([]);
        },
      });
    }
  }

  ngOnDestroy() {
    this.cleanup?.();
  }
}
